import { useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import { useUIStore } from '../store/uiStore';

/**
 * Component that blocks browser navigation (back/forward buttons, URL changes,
 * <Link> clicks) when navigation is locked due to unsaved changes.
 *
 * Flow:
 *  1. Router blocks a navigation -> we store it as a pending 'blocker'
 *     navigation and open NavigationConfirmModal.
 *  2. The modal either cancels (pendingNavigation cleared, still locked) or
 *     confirms (pendingNavigation cleared, unlocked). We react to that here by
 *     calling blocker.reset() or blocker.proceed() so the router blocker never
 *     stays stuck in the 'blocked' state.
 */
export function NavigationBlocker() {
  const navigationLock = useUIStore((state) => state.navigationLock);
  // True once the current blocked navigation has been handed to the modal.
  const handedToModalRef = useRef(false);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      navigationLock.isLocked && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      handedToModalRef.current = false;
      return;
    }

    if (!handedToModalRef.current) {
      handedToModalRef.current = true;
      useUIStore.setState((state) => ({
        navigationLock: {
          ...state.navigationLock,
          pendingNavigation: {
            path: blocker.location.pathname,
            options: { state: blocker.location.state },
            type: 'blocker' as const,
          },
        },
        modals: {
          ...state.modals,
          navigationConfirm: true,
        },
      }));
      return;
    }

    // The modal has answered: pendingNavigation is cleared in both cases.
    if (!navigationLock.pendingNavigation) {
      if (navigationLock.isLocked) {
        blocker.reset(); // "Stay here"
      } else {
        blocker.proceed(); // "Leave anyway"
      }
    }
  }, [blocker, navigationLock.isLocked, navigationLock.pendingNavigation]);

  return null;
}
